import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { statsAPI, postsAPI, adeverintaAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import AdminLayout from './AdminLayout';
import {
  FileText,
  FileBadge,
  Users,
  Clock,
  Plus,
  ArrowRight,
  TrendingUp,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const AdminDashboard = () => {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [pendingAdeverinte, setPendingAdeverinte] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, postsData, adeverinteData] = await Promise.all([
          statsAPI.get(),
          postsAPI.getAll({ limit: 5 }),
          adeverintaAPI.getAll('pending'),
        ]);
        setStats(statsData);
        setRecentPosts(postsData.slice(0, 5));
        setPendingAdeverinte(adeverinteData.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'en' ? 'en-GB' : 'ro-RO', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-stone-200 rounded w-1/4"></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-stone-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-stone-800">{t('admin.dashboard')}</h1>
          <p className="text-stone-500 text-sm">
            {lang === 'en' ? 'Welcome back' : 'Bine ai revenit'}
          </p>
        </div>
        <Link to="/admin/posts/new">
          <Button className="bg-islamic-teal hover:bg-islamic-teal-light gap-2" data-testid="create-post-btn">
            <Plus className="w-4 h-4" />
            {t('admin.createPost')}
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="border-stone-200" data-testid="stat-posts">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500">{t('admin.stats.totalPosts')}</p>
                <p className="text-2xl font-semibold text-stone-800 mt-1">{stats?.posts_total || 0}</p>
                <p className="text-xs text-islamic-teal mt-1">
                  {stats?.posts_published || 0} {t('admin.stats.publishedPosts').toLowerCase()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-islamic-teal" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200" data-testid="stat-certificates">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500">{t('admin.stats.totalCertificates')}</p>
                <p className="text-2xl font-semibold text-stone-800 mt-1">{stats?.adeverinte_total || 0}</p>
                <p className="text-xs text-yellow-600 mt-1">
                  {stats?.adeverinte_pending || 0} {t('admin.stats.pendingCertificates').toLowerCase()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-islamic-gold/10 flex items-center justify-center">
                <FileBadge className="w-5 h-5 text-islamic-gold" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200" data-testid="stat-signed">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500">{t('admin.stats.signedCertificates')}</p>
                <p className="text-2xl font-semibold text-stone-800 mt-1">{stats?.adeverinte_signed || 0}</p>
                <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Completed
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200" data-testid="stat-users">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500">{t('admin.stats.totalUsers')}</p>
                <p className="text-2xl font-semibold text-stone-800 mt-1">{stats?.users_count || 0}</p>
                <p className="text-xs text-stone-500 mt-1">Admin accounts</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-stone-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Posts */}
        <Card className="border-stone-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <h2 className="font-semibold text-stone-800">{t('home.latestNews')}</h2>
              <Link to="/admin/posts">
                <Button variant="ghost" size="sm" className="text-islamic-teal gap-1">
                  View all <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="divide-y divide-stone-100">
              {recentPosts.length > 0 ? recentPosts.map((post) => (
                <Link 
                  key={post.id} 
                  to={`/admin/posts/${post.id}`}
                  className="flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
                  data-testid={`recent-post-${post.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">
                      {post[`title_${lang}`] || post.title_ro || post.title_en}
                    </p>
                    <p className="text-xs text-stone-500 mt-1 flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {formatDate(post.created_at)}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    post.status === 'published' 
                      ? 'bg-teal-100 text-teal-700' 
                      : 'bg-stone-100 text-stone-600'
                  }`}>
                    {post.status}
                  </span>
                </Link>
              )) : (
                <div className="p-8 text-center text-stone-500">
                  {t('common.noResults')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pending Adeverinte */}
        <Card className="border-stone-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <h2 className="font-semibold text-stone-800 flex items-center gap-2">
                {t('admin.stats.pendingCertificates')}
                {pendingAdeverinte.length > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                    {pendingAdeverinte.length}
                  </span>
                )}
              </h2>
              <Link to="/admin/adeverinta">
                <Button variant="ghost" size="sm" className="text-islamic-teal gap-1">
                  View all <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="divide-y divide-stone-100">
              {pendingAdeverinte.length > 0 ? pendingAdeverinte.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/admin/adeverinta/${item.id}`}
                  className="flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
                  data-testid={`pending-adeverinta-${item.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800">
                      {item.nume} {item.prenume}
                    </p>
                    <p className="text-xs text-stone-500 mt-1">{item.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                    <span className="text-xs text-stone-500">{formatDate(item.created_at)}</span>
                  </div>
                </Link>
              )) : (
                <div className="p-8 text-center text-stone-500">
                  {lang === 'en' ? 'No pending certificates' : 'Nicio adeverință în așteptare'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
